import { useResetOnChange } from '@/hooks/useResetOnChange'
import { Skeleton } from '@/components/ui/skeleton'
import { cn, getAvatarColor, getInitials } from '@/lib/utils'
import type { UserProfile } from '@/types'

const SIZE_CLASS = {
  xs: 'h-6 w-6 text-[11px]',
  sm: 'h-7 w-7 text-xs',
  md: 'h-8 w-8 text-sm',
  lg: 'h-10 w-10 text-base',
  xl: 'h-16 w-16 text-2xl',
  '2xl': 'h-24 w-24 text-4xl',
  '3xl': 'h-28 w-28 text-5xl',
} as const

export type UserAvatarSize = keyof typeof SIZE_CLASS

interface Props {
  loading?: boolean
  profile: (Pick<UserProfile, 'uid' | 'displayName'> & { photoURL?: string | null }) | undefined
  fallbackName?: string
  size?: UserAvatarSize
  className?: string
}

export function UserAvatar({ loading, profile, fallbackName, size = 'md', className }: Props) {
  const photoURL = profile?.photoURL
  // A new photo deserves a fresh attempt — clear the previous one's failure.
  const [imageFailed, setImageFailed] = useResetOnChange(photoURL, () => false)
  const name = profile?.displayName ?? fallbackName ?? '?'
  const initial = getInitials(name).slice(0, 1)
  const showImage = !!profile?.photoURL && !imageFailed
  const colorClass = profile?.uid
    ? `${getAvatarColor(profile.uid)} text-primary-foreground`
    : 'bg-muted'

  if (loading) return <Skeleton className={cn('shrink-0 rounded-full', SIZE_CLASS[size], className)} />

  return (
    <div
      className={cn(
        'shrink-0 rounded-full overflow-hidden flex items-center justify-center font-bold',
        SIZE_CLASS[size],
        showImage ? 'bg-muted' : colorClass,
        className,
      )}
    >
      {showImage ? (
        <img
          src={profile!.photoURL!}
          alt={profile?.displayName ?? ''}
          referrerPolicy="no-referrer"
          className="h-full w-full object-cover"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <span>{initial}</span>
      )}
    </div>
  )
}
